import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    recipientEmail: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message || !formData.recipientEmail) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        recipient_email: formData.recipientEmail,
      });

      if (response.data.status === 'success') {
        toast.success('Message sent successfully!');
        setFormData({
          name: '',
          email: '',
          message: '',
          recipientEmail: '',
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 px-6 md:px-12 relative">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-blue-500 font-mono mb-4" data-testid="contact-label">
            Get In Touch
          </p>
          <h2 className="text-4xl md:text-5xl font-outfit font-semibold tracking-tight" data-testid="contact-title">
            Let's Build Something Amazing
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="contact-info"
          >
            <h3 className="text-2xl md:text-3xl font-outfit font-medium mb-8">
              Contact Information
            </h3>

            <div className="space-y-6">
              <div className="flex items-start" data-testid="contact-phone">
                <div className="w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <Phone className="text-blue-500" size={20} />
                </div>
                <div>
                  <p className="text-slate-400 font-inter text-sm mb-1">Phone</p>
                  <a
                    href="tel:088228079946"
                    className="text-white font-inter hover:text-blue-400 transition-colors"
                  >
                    +62 882-2807-9946
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-testid="contact-email-info">
                <div className="w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <Mail className="text-blue-500" size={20} />
                </div>
                <div>
                  <p className="text-slate-400 font-inter text-sm mb-1">Email</p>
                  <p className="text-white font-inter">
                    admin@quantumbytestudio.co.id
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <MapPin className="text-blue-500" size={20} />
                </div>
                <div>
                  <p className="text-slate-400 font-inter text-sm mb-1">Address</p>
                  <p className="text-white font-inter">
                    Jl. Semeru VI I-8 Jember
                  </p>
                </div>
              </div>

            </div>

            <div className="mt-12 p-8 bg-slate-950/70 backdrop-blur-xl border border-slate-800/50 rounded-2xl">
              <h4 className="text-xl font-outfit font-medium mb-4">Why Choose Us?</h4>
              <ul className="space-y-3 text-slate-400 font-inter">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  Expert team with 5+ years experience
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  Agile development methodology
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  24/7 support and maintenance
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="contact-form-container"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-slate-300 font-inter mb-2 block">
                  Your Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-form-name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="bg-slate-900/50 border-slate-800 text-white focus:border-blue-500 focus:ring-blue-500/20"
                  required
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-300 font-inter mb-2 block">
                  Your Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  data-testid="contact-form-email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="bg-slate-900/50 border-slate-800 text-white focus:border-blue-500 focus:ring-blue-500/20"
                  required
                />
              </div>

              <div>
                <Label htmlFor="recipientEmail" className="text-slate-300 font-inter mb-2 block">
                  Send To (Recipient Email)
                </Label>
                <Input
                  id="recipientEmail"
                  name="recipientEmail"
                  data-testid="contact-form-recipient-email"
                  type="email"
                  value={formData.recipientEmail}
                  onChange={handleChange}
                  placeholder="admin@quantumbytestudio.co.id"
                  className="bg-slate-900/50 border-slate-800 text-white focus:border-blue-500 focus:ring-blue-500/20"
                  required
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-300 font-inter mb-2 block">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-form-message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  rows={6}
                  className="bg-slate-900/50 border-slate-800 text-white focus:border-blue-500 focus:ring-blue-500/20 resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-form-submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-lg font-inter font-medium transition-all duration-200 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2" size={18} />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;